import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { gql } from '@apollo/client'
import { apolloClient } from '../utils/apollo.js'
import { t } from '../utils/i18n.js'
import type {
  User,
  Role,
  Language,
  UserStatus,
  UserRole,
  UsersData,
  RolesData,
  LanguagesData,
  UserStatusesData,
  UserRolesData,
} from '../models/user.js'
import styles from './user-management-page.css?inline'

const GET_USERS_QUERY = gql`
  query GetUsers {
    users {
      id
      username
      email
      person { id name familyName }
      status { id name }
    }
  }
`

const GET_USER_QUERY = gql`
  query GetUser($id: ID!) {
    user(id: $id) {
      id
      username
      email
      person { id name familyName }
      status { id name }
    }
  }
`

const GET_ROLES_QUERY = gql`
  query GetRoles {
    roles {
      id
      name
    }
  }
`

const GET_LANGUAGES_QUERY = gql`
  query GetLanguages {
    languages {
      id
      name
    }
  }
`

const GET_USER_STATUSES_QUERY = gql`
  query GetUserStatuses {
    userStatuses {
      id
      name
    }
  }
`

const GET_SPELLINGS_QUERY = gql`
  query GetSpellings {
    spellings {
      id
      spelling
      meaning {
        id
        word { id }
        language { id }
      }
      system { id }
    }
  }
`

const GET_USER_ROLES_QUERY = gql`
  query GetUserRoles {
    userRoles {
      id
      user { id username }
      role { id name }
      language { id name }
    }
  }
`

const CREATE_PERSON_MUTATION = gql`
  mutation CreatePerson($name: String!, $familyName: String!) {
    createPerson(input: { name: $name, familyName: $familyName }) {
      id
    }
  }
`

const CREATE_USER_MUTATION = gql`
  mutation CreateUser($personId: ID!, $statusId: ID!, $username: String!, $email: String!, $password: String!) {
    createUser(input: {
      personId: $personId
      statusId: $statusId
      username: $username
      email: $email
      password: $password
    }) {
      id
      username
    }
  }
`

const CREATE_USER_ROLE_MUTATION = gql`
  mutation CreateUserRole($userId: ID!, $roleId: ID!, $languageId: ID!) {
    createUserRole(input: {
      userId: $userId
      roleId: $roleId
      languageId: $languageId
    }) {
      id
    }
  }
`

const UPDATE_PERSON_MUTATION = gql`
  mutation UpdatePerson($id: ID!, $name: String, $familyName: String) {
    updatePerson(input: { id: $id, name: $name, familyName: $familyName }) {
      id
    }
  }
`

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser($id: ID!, $personId: ID, $statusId: ID, $username: String, $email: String, $password: String) {
    updateUser(input: { id: $id, personId: $personId, statusId: $statusId, username: $username, email: $email, password: $password }) {
      id
    }
  }
`

const DELETE_USER_ROLE_MUTATION = gql`
  mutation DeleteUserRole($id: ID!) {
    deleteUserRole(id: $id)
  }
`

const RESET_USER_MFA_MUTATION = gql`
  mutation ResetUserMfa($userId: ID!) {
    resetUserMfa(userId: $userId)
  }
`

@customElement('user-management-page')
export class UserManagementPage extends LitElement {
  @state() private users: User[] = []
  @state() private roles: Role[] = []
  @state() private languages: Language[] = []
  @state() private userStatuses: UserStatus[] = []
  @state() private userRoles: UserRole[] = []
  @state() private spellings: Array<{
    id: string
    spelling: string
    meaning: { id: string; word: { id: string }; language: { id: string } }
    system: { id: string }
  }> = []
  @state() private loading = true
  @state() private error = ''
  @state() private showCreateForm = false
  @state() private creating = false
  // Form state
  @state() private newUserName = ''
  @state() private newUserFamilyName = ''
  @state() private newUsername = ''
  @state() private newEmail = ''
  @state() private newPassword = ''
  @state() private selectedStatusId = ''
  /** Initial roles to assign when creating user: list of { roleId, languageId } */
  @state() private selectedCreateRoles: Array<{ roleId: string; languageId: string }> = []
  // Search
  @state() private searchQuery = ''
  @state() private searchField: 'email' | 'username' | 'name' | 'familyName' | 'id' = 'username'
  @state() private searchMode: 'contains' | 'equals' = 'contains'
  // Edit user
  @state() private editingUser: User | null = null
  @state() private editName = ''
  @state() private editFamilyName = ''
  @state() private editUsername = ''
  @state() private editEmail = ''
  @state() private editPassword = ''
  @state() private editStatusId = ''
  @state() private removedUserRoleIds: string[] = []
  @state() private editNewRoles: Array<{ roleId: string; languageId: string }> = []
  @state() private saving = false
  @state() private editLoading = false
  @state() private resetMfaLoading = false

  connectedCallback() {
    super.connectedCallback()
    this.loadData()
    // Listen for i18n updates
    window.addEventListener('i18n-updated', () => {
      this.requestUpdate()
    })
  }

  private async loadData() {
    this.loading = true
    this.error = ''
    try {
      const [usersResult, rolesResult, languagesResult, statusesResult, userRolesResult, spellingsResult] = await Promise.all([
        apolloClient.query<UsersData>({ query: GET_USERS_QUERY }),
        apolloClient.query<RolesData>({ query: GET_ROLES_QUERY }),
        apolloClient.query<LanguagesData>({ query: GET_LANGUAGES_QUERY }),
        apolloClient.query<UserStatusesData>({ query: GET_USER_STATUSES_QUERY }),
        apolloClient.query<UserRolesData>({ query: GET_USER_ROLES_QUERY }),
        apolloClient.query<{ spellings: Array<{
          id: string
          spelling: string
          meaning: { id: string; word: { id: string }; language: { id: string } }
          system: { id: string }
        }> }>({ query: GET_SPELLINGS_QUERY }),
      ])

      if (usersResult.error) {
        const gqlErrors = (usersResult.error as { graphQLErrors?: Array<{ message: string }> }).graphQLErrors
        const msg = gqlErrors?.map((e) => e.message).join(', ') ?? usersResult.error.message ?? ''
        if (msg.toLowerCase().includes('authentication required')) {
          throw new Error(t('userManagement.errorAuthRequired'))
        }
        throw new Error(msg || usersResult.error.message)
      }
      if (rolesResult.error) throw new Error(rolesResult.error.message)
      if (languagesResult.error) throw new Error(languagesResult.error.message)
      if (statusesResult.error) throw new Error(statusesResult.error.message)
      if (userRolesResult.error) throw new Error(userRolesResult.error.message)
      if (spellingsResult.error) throw new Error(spellingsResult.error.message)

      this.users = usersResult.data?.users || []
      this.roles = rolesResult.data?.roles || []
      this.languages = languagesResult.data?.languages || []
      this.userStatuses = statusesResult.data?.userStatuses || []
      this.userRoles = userRolesResult.data?.userRoles || []
      this.spellings = spellingsResult.data?.spellings || []

      if (this.userStatuses.length > 0 && !this.selectedStatusId) {
        this.selectedStatusId = this.userStatuses[0].id
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : t('userManagement.errorFailed')
    } finally {
      this.loading = false
    }
  }

  private get filteredUsers(): User[] {
    const q = this.searchQuery.trim().toLowerCase()
    if (!q) return this.users
    const isEquals = this.searchMode === 'equals'
    return this.users.filter((user) => {
      let value: string
      switch (this.searchField) {
        case 'email':
          value = user.email ?? ''
          break
        case 'username':
          value = user.username ?? ''
          break
        case 'name':
          value = user.person?.name ?? ''
          break
        case 'familyName':
          value = user.person?.familyName ?? ''
          break
        case 'id':
          value = user.id ?? ''
          break
        default:
          value = ''
      }
      const v = value.toLowerCase()
      return isEquals ? v === q : v.includes(q)
    })
  }

  /** Refetch only users and user roles (e.g. after create or assign role). Does not set loading. */
  private async refetchUsersAndRoles() {
    try {
      const [usersResult, userRolesResult] = await Promise.all([
        apolloClient.query<UsersData>({ query: GET_USERS_QUERY }),
        apolloClient.query<UserRolesData>({ query: GET_USER_ROLES_QUERY }),
      ])
      if (usersResult.error) throw new Error(usersResult.error.message)
      if (userRolesResult.error) throw new Error(userRolesResult.error.message)
      this.users = usersResult.data?.users ?? []
      this.userRoles = userRolesResult.data?.userRoles ?? []
    } catch (err) {
      this.error = err instanceof Error ? err.message : t('userManagement.errorFailed')
    }
  }

  private async onCreateUser(e: SubmitEvent) {
    e.preventDefault()
    this.creating = true
    this.error = ''

    try {
      // Step 1: Create person (API expects name and familyName strings)
      const personResult = await apolloClient.mutate<{ createPerson: { id: string } }>({
        mutation: CREATE_PERSON_MUTATION,
        variables: { name: this.newUserName, familyName: this.newUserFamilyName },
      })
      if (personResult.error) {
        const msg = (personResult.error as { graphQLErrors?: Array<{ message: string }> }).graphQLErrors?.map((e: { message: string }) => e.message).join(', ') ?? personResult.error.message
        throw new Error(msg)
      }
      const personId = personResult.data?.createPerson?.id
      if (!personId) throw new Error('Failed to create person')

      // Step 2: Create user
      const userResult = await apolloClient.mutate<{ createUser: { id: string; username: string } }>({
        mutation: CREATE_USER_MUTATION,
        variables: {
          personId,
          statusId: this.selectedStatusId,
          username: this.newUsername,
          email: this.newEmail,
          password: this.newPassword,
        },
      })
      if (userResult.error) {
        const msg = (userResult.error as { graphQLErrors?: Array<{ message: string }> }).graphQLErrors?.map((e: { message: string }) => e.message).join(', ') ?? userResult.error.message
        throw new Error(msg)
      }
      const newUserId = userResult.data?.createUser?.id
      if (!newUserId) throw new Error('Failed to create user')

      // Step 3: Assign selected roles
      for (const { roleId, languageId } of this.selectedCreateRoles) {
        if (!roleId || !languageId) continue
        const roleResult = await apolloClient.mutate<{ createUserRole: { id: string } }>({
          mutation: CREATE_USER_ROLE_MUTATION,
          variables: { userId: newUserId, roleId, languageId },
        })
        if (roleResult.error) {
          const msg = (roleResult.error as { graphQLErrors?: Array<{ message: string }> }).graphQLErrors?.map((e: { message: string }) => e.message).join(', ') ?? roleResult.error.message
          throw new Error(msg)
        }
      }

      // Reset form
      this.newUserName = ''
      this.newUserFamilyName = ''
      this.newUsername = ''
      this.newEmail = ''
      this.newPassword = ''
      this.selectedCreateRoles = []
      this.showCreateForm = false

      await this.refetchUsersAndRoles()
    } catch (err) {
      this.error = err instanceof Error ? err.message : t('userManagement.errorCreate')
    } finally {
      this.creating = false
    }
  }

  private async openEdit(user: User) {
    this.editingUser = user
    this.editName = user.person?.name ?? ''
    this.editFamilyName = user.person?.familyName ?? ''
    this.editUsername = user.username ?? ''
    this.editEmail = user.email ?? ''
    this.editPassword = ''
    this.editStatusId = String(user.status?.id ?? this.userStatuses[0]?.id ?? '')
    this.removedUserRoleIds = []
    this.editNewRoles = []
    this.error = ''
    this.editLoading = true
    try {
      const [userResult, userRolesResult] = await Promise.all([
        apolloClient.query<{ user: User | null }>({ query: GET_USER_QUERY, variables: { id: user.id }, fetchPolicy: 'network-only' }),
        apolloClient.query<UserRolesData>({ query: GET_USER_ROLES_QUERY }),
      ])
      if (userResult.data?.user) {
        const fetchedUser = userResult.data.user
        this.editingUser = fetchedUser
        this.editName = fetchedUser.person?.name ?? ''
        this.editFamilyName = fetchedUser.person?.familyName ?? ''
        this.editUsername = fetchedUser.username ?? ''
        this.editEmail = fetchedUser.email ?? ''
        const statusId = fetchedUser.status?.id ?? this.editStatusId
        this.editStatusId = String(statusId)
      }
      if (userRolesResult.data?.userRoles) {
        this.userRoles = userRolesResult.data.userRoles
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : t('userManagement.errorFailed')
    } finally {
      this.editLoading = false
    }
  }

  private closeEdit() {
    this.editingUser = null
    this.error = ''
  }

  private async onResetMfa() {
    if (!this.editingUser || this.resetMfaLoading) return
    if (!window.confirm(t('userManagement.resetMfaConfirm'))) return
    this.resetMfaLoading = true
    this.error = ''
    try {
      const { error } = await apolloClient.mutate<{ resetUserMfa: boolean }>({
        mutation: RESET_USER_MFA_MUTATION,
        variables: { userId: this.editingUser.id },
      })
      if (error) {
        const msg = (error as { graphQLErrors?: Array<{ message: string }> }).graphQLErrors?.map((e: { message: string }) => e.message).join(', ') ?? error.message
        throw new Error(msg)
      }
      this.error = '' // clear any previous error
      alert(t('userManagement.resetMfaSuccess'))
    } catch (err) {
      this.error = err instanceof Error ? err.message : t('userManagement.errorResetMfa')
    } finally {
      this.resetMfaLoading = false
    }
  }

  private getEditFormCurrentRoles(): UserRole[] {
    if (!this.editingUser) return []
    return this.getUserRoles(this.editingUser.id).filter((ur) => !this.removedUserRoleIds.includes(ur.id))
  }

  private async onSaveUser(e: SubmitEvent) {
    e.preventDefault()
    if (!this.editingUser) return
    this.saving = true
    this.error = ''
    try {
      const personId = this.editingUser.person?.id
      if (personId) {
        const personRes = await apolloClient.mutate<{ updatePerson: { id: string } }>({
          mutation: UPDATE_PERSON_MUTATION,
          variables: { id: personId, name: this.editName, familyName: this.editFamilyName },
        })
        if (personRes.error) throw new Error(personRes.error.message)
      }
      const userInput: Record<string, unknown> = {
        id: this.editingUser.id,
        personId: this.editingUser.person?.id,
        statusId: this.editStatusId,
        username: this.editUsername,
        email: this.editEmail,
      }
      if (this.editPassword.trim()) userInput.password = this.editPassword.trim()
      const userRes = await apolloClient.mutate<{ updateUser: { id: string } }>({
        mutation: UPDATE_USER_MUTATION,
        variables: userInput,
      })
      if (userRes.error) throw new Error(userRes.error.message)
      for (const id of this.removedUserRoleIds) {
        const delRes = await apolloClient.mutate<{ deleteUserRole: boolean }>({
          mutation: DELETE_USER_ROLE_MUTATION,
          variables: { id },
        })
        if (delRes.error) throw new Error(delRes.error.message)
      }
      for (const { roleId, languageId } of this.editNewRoles) {
        if (!roleId || !languageId) continue
        const roleRes = await apolloClient.mutate<{ createUserRole: { id: string } }>({
          mutation: CREATE_USER_ROLE_MUTATION,
          variables: { userId: this.editingUser.id, roleId, languageId },
        })
        if (roleRes.error) throw new Error(roleRes.error.message)
      }
      this.closeEdit()
      await this.refetchUsersAndRoles()
    } catch (err) {
      this.error = err instanceof Error ? err.message : t('userManagement.errorUpdate')
    } finally {
      this.saving = false
    }
  }

  private getUserRoles(userId: string): UserRole[] {
    return this.userRoles.filter(ur => ur.user.id === userId)
  }

  static styles = css`${unsafeCSS(styles)}`

  render() {
    if (this.loading) {
      return html`<div class="page"><p>${t('common.loading')}</p></div>`
    }

    return html`
      <div class="page">
        <h1>${t('userManagement.title')}</h1>
        ${this.error ? html`<div class="error">${this.error}</div>` : ''}

        <div class="create-form">
          <h2 style="margin-top: 0; font-size: 1.2rem;">${t('userManagement.createNewUser')}</h2>
          ${this.showCreateForm ? html`
            <form @submit=${this.onCreateUser}>
              <div class="form-row">
                <div class="form-field">
                  <label for="name">${t('userManagement.name')}</label>
                  <input
                    id="name"
                    type="text"
                    data-testid="create-user-name"
                    .value=${this.newUserName}
                    @input=${(e: Event) => { this.newUserName = (e.target as HTMLInputElement).value }}
                    required
                    ?disabled=${this.creating}
                  />
                </div>
                <div class="form-field">
                  <label for="familyName">${t('userManagement.familyName')}</label>
                  <input
                    id="familyName"
                    type="text"
                    data-testid="create-user-familyName"
                    .value=${this.newUserFamilyName}
                    @input=${(e: Event) => { this.newUserFamilyName = (e.target as HTMLInputElement).value }}
                    required
                    ?disabled=${this.creating}
                  />
                </div>
              </div>
              <div class="form-row">
                <div class="form-field">
                  <label for="username">${t('login.username')}</label>
                  <input
                    id="username"
                    type="text"
                    data-testid="create-user-username"
                    .value=${this.newUsername}
                    @input=${(e: Event) => { this.newUsername = (e.target as HTMLInputElement).value }}
                    required
                    ?disabled=${this.creating}
                  />
                </div>
                <div class="form-field">
                  <label for="email">${t('userManagement.email')}</label>
                  <input
                    id="email"
                    type="email"
                    data-testid="create-user-email"
                    .value=${this.newEmail}
                    @input=${(e: Event) => { this.newEmail = (e.target as HTMLInputElement).value }}
                    required
                    ?disabled=${this.creating}
                  />
                </div>
              </div>
              <div class="form-row">
                <div class="form-field">
                  <label for="password">${t('userManagement.password')}</label>
                  <input
                    id="password"
                    type="password"
                    data-testid="create-user-password"
                    .value=${this.newPassword}
                    @input=${(e: Event) => { this.newPassword = (e.target as HTMLInputElement).value }}
                    required
                    ?disabled=${this.creating}
                  />
                </div>
                <div class="form-field">
                  <label for="status">${t('userManagement.status')}</label>
                  <select
                    id="status"
                    .value=${this.selectedStatusId}
                    @change=${(e: Event) => { this.selectedStatusId = (e.target as HTMLSelectElement).value }}
                    ?disabled=${this.creating}
                  >
                    ${this.userStatuses.map(status => {
                      // UserStatus.name is an i18n key like "user_status_2.name"
                      // Translate it using the i18n function
                      const displayName = t(status.name) || `Status ${status.id}`
                      return html`
                        <option value=${status.id}>${displayName}</option>
                      `
                    })}
                  </select>
                </div>
              </div>
              <div class="form-row form-section">
                <label class="section-label">${t('userManagement.initialRoles')}</label>
                <div class="roles-rows">
                  ${this.selectedCreateRoles.map((row, index) => html`
                    <div class="role-row">
                      <select
                        .value=${row.roleId}
                        @change=${(e: Event) => {
                          const val = (e.target as HTMLSelectElement).value
                          this.selectedCreateRoles = this.selectedCreateRoles.map((r, i) => i === index ? { ...r, roleId: val } : r)
                        }}
                        ?disabled=${this.creating}
                      >
                        ${this.roles.map(role => {
                          const displayName = t(role.name) || `Role ${role.id}`
                          return html`<option value=${role.id}>${displayName}</option>`
                        })}
                      </select>
                      <select
                        .value=${row.languageId}
                        @change=${(e: Event) => {
                          const val = (e.target as HTMLSelectElement).value
                          this.selectedCreateRoles = this.selectedCreateRoles.map((r, i) => i === index ? { ...r, languageId: val } : r)
                        }}
                        ?disabled=${this.creating}
                      >
                        ${this.languages.map(lang => {
                          const displayName = t(lang.name) || `Language ${lang.id}`
                          return html`<option value=${lang.id}>${displayName}</option>`
                        })}
                      </select>
                      <button
                        type="button"
                        @click=${() => { this.selectedCreateRoles = this.selectedCreateRoles.filter((_, i) => i !== index) }}
                        ?disabled=${this.creating}
                        title=${t('userManagement.removeRole')}
                      >${t('userManagement.removeRole')}</button>
                    </div>
                  `)}
                  <button
                    type="button"
                    class="add-role"
                    @click=${() => {
                      const roleId = this.roles[0]?.id ?? ''
                      const languageId = this.languages[0]?.id ?? ''
                      this.selectedCreateRoles = [...this.selectedCreateRoles, { roleId, languageId }]
                    }}
                    ?disabled=${this.creating || this.roles.length === 0 || this.languages.length === 0}
                  >${t('userManagement.addRole')}</button>
                </div>
              </div>
              <div class="form-actions">
                <button type="button" @click=${() => { this.showCreateForm = false }} ?disabled=${this.creating}>
                  ${t('userManagement.cancel')}
                </button>
                <button type="submit" data-testid="create-user-submit" ?disabled=${this.creating}>
                  ${this.creating ? t('userManagement.creating') : t('userManagement.createUser')}
                </button>
              </div>
            </form>
          ` : html`
            <button type="button" data-testid="create-user-open" @click=${() => { this.showCreateForm = true }}>${t('userManagement.createNewUser')}</button>
          `}
        </div>

        <div class="users-list" data-spellings-count=${this.spellings.length}>
          <h2 style="margin-top: 0; font-size: 1.2rem;">${t('userManagement.users')}</h2>
          <div class="search-bar">
            <input
              type="text"
              class="search-input"
              placeholder=${t('userManagement.searchPlaceholder')}
              .value=${this.searchQuery}
              @input=${(e: Event) => { this.searchQuery = (e.target as HTMLInputElement).value }}
            />
            <select
              class="search-field-select"
              .value=${this.searchField}
              @change=${(e: Event) => { this.searchField = (e.target as HTMLSelectElement).value as typeof this.searchField }}
            >
              <option value="username">${t('userManagement.searchByUsername')}</option>
              <option value="email">${t('userManagement.searchByEmail')}</option>
              <option value="name">${t('userManagement.searchByName')}</option>
              <option value="familyName">${t('userManagement.searchByFamilyName')}</option>
              <option value="id">${t('userManagement.searchById')}</option>
            </select>
            <div class="search-mode-toggle">
              <button
                type="button"
                class="toggle-option"
                ?data-active=${this.searchMode === 'contains'}
                @click=${() => { this.searchMode = 'contains' }}
              >${t('userManagement.contains')}</button>
              <button
                type="button"
                class="toggle-option"
                ?data-active=${this.searchMode === 'equals'}
                @click=${() => { this.searchMode = 'equals' }}
              >${t('userManagement.equals')}</button>
            </div>
          </div>
          ${this.users.length === 0 ? html`<p>${t('userManagement.noUsers')}</p>` : ''}
          <div class="users-table-wrap">
            <table class="users-table">
              <thead>
                <tr>
                  <th>${t('login.username')}</th>
                  <th>${t('userManagement.email')}</th>
                  <th>${t('userManagement.name')}</th>
                  <th>${t('userManagement.familyName')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                ${this.filteredUsers.map(user => html`
                  <tr>
                    <td>${user.username}</td>
                    <td>${user.email}</td>
                    <td>${user.person?.name ?? ''}</td>
                    <td>${user.person?.familyName ?? ''}</td>
                    <td class="cell-actions">
                      <button type="button" class="btn-edit" data-testid="edit-user-btn" @click=${() => this.openEdit(user)}>${t('userManagement.edit')}</button>
                    </td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>

          ${this.editingUser ? html`
            <div class="edit-form" data-testid="edit-user-form">
              <h3>${t('userManagement.editUser')}</h3>
              ${this.editLoading ? html`<p class="edit-loading">${t('userManagement.loadingUser')}</p>` : ''}
              <form @submit=${this.onSaveUser}>
                <div class="form-row">
                  <div class="form-field">
                    <label for="edit-name">${t('userManagement.name')}</label>
                    <input id="edit-name" type="text" .value=${this.editName} @input=${(e: Event) => { this.editName = (e.target as HTMLInputElement).value }} ?disabled=${this.saving || this.editLoading} required />
                  </div>
                  <div class="form-field">
                    <label for="edit-familyName">${t('userManagement.familyName')}</label>
                    <input id="edit-familyName" type="text" .value=${this.editFamilyName} @input=${(e: Event) => { this.editFamilyName = (e.target as HTMLInputElement).value }} ?disabled=${this.saving || this.editLoading} required />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-field">
                    <label for="edit-username">${t('login.username')}</label>
                    <input id="edit-username" type="text" .value=${this.editUsername} @input=${(e: Event) => { this.editUsername = (e.target as HTMLInputElement).value }} ?disabled=${this.saving || this.editLoading} required />
                  </div>
                  <div class="form-field">
                    <label for="edit-email">${t('userManagement.email')}</label>
                    <input id="edit-email" type="email" .value=${this.editEmail} @input=${(e: Event) => { this.editEmail = (e.target as HTMLInputElement).value }} ?disabled=${this.saving || this.editLoading} required />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-field">
                    <label for="edit-password">${t('userManagement.password')} (${t('userManagement.optional')})</label>
                    <input id="edit-password" type="password" .value=${this.editPassword} @input=${(e: Event) => { this.editPassword = (e.target as HTMLInputElement).value }} ?disabled=${this.saving || this.editLoading} placeholder=${t('userManagement.passwordLeaveEmpty')} />
                  </div>
                  <div class="form-field">
                    <label for="edit-status">${t('userManagement.status')}</label>
                    <select id="edit-status" .value=${this.editStatusId} @change=${(e: Event) => { this.editStatusId = (e.target as HTMLSelectElement).value }} ?disabled=${this.saving || this.editLoading}>
                      ${this.userStatuses.map(s => html`<option value=${String(s.id)} ?selected=${String(this.editStatusId) === String(s.id)}>${t(s.name) || s.id}</option>`)}
                    </select>
                  </div>
                </div>
                <div class="form-row form-section">
                  <label class="section-label">${t('userManagement.roles')}</label>
                  <div class="roles-rows">
                    <div class="current-roles-block">
                      <span class="current-roles-label">${t('userManagement.currentRoles')}</span>
                      ${this.getEditFormCurrentRoles().length === 0
                        ? html`<p class="roles-empty">${t('userManagement.noRolesAssigned')}</p>`
                        : html`
                          <ul class="current-roles-list">
                            ${this.getEditFormCurrentRoles().map(ur => html`
                              <li class="role-row">
                                <span class="role-badge">${t(ur.role.name) || ur.role.id} (${t(ur.language?.name) ?? ur.language?.id ?? ''})</span>

                                <button type="button" @click=${() => { this.removedUserRoleIds = [...this.removedUserRoleIds, ur.id] }} ?disabled=${this.saving || this.editLoading}>${t('userManagement.removeRole')}</button>
                              </li>
                            `)}
                          </ul>
                        `}
                    </div>
                    ${this.editNewRoles.map((row, idx) => html`
                      <div class="role-row">
                        <select .value=${row.roleId} @change=${(e: Event) => { const v = (e.target as HTMLSelectElement).value; this.editNewRoles = this.editNewRoles.map((r, i) => i === idx ? { ...r, roleId: v } : r) }} ?disabled=${this.saving || this.editLoading}>
                          ${this.roles.map(r => html`<option value=${r.id}>${t(r.name) || r.id}</option>`)}
                        </select>
                        <select .value=${row.languageId} @change=${(e: Event) => { const v = (e.target as HTMLSelectElement).value; this.editNewRoles = this.editNewRoles.map((r, i) => i === idx ? { ...r, languageId: v } : r) }} ?disabled=${this.saving || this.editLoading}>
                          ${this.languages.map(l => html`<option value=${l.id}>${t(l.name) || l.id}</option>`)}
                        </select>
                        <button type="button" @click=${() => { this.editNewRoles = this.editNewRoles.filter((_, i) => i !== idx) }} ?disabled=${this.saving || this.editLoading}>${t('userManagement.removeRole')}</button>
                      </div>
                    `)}
                    <div class="add-role-block">
                      <button type="button" class="add-role" @click=${() => { this.editNewRoles = [...this.editNewRoles, { roleId: this.roles[0]?.id ?? '', languageId: this.languages[0]?.id ?? '' }] }} ?disabled=${this.saving || this.editLoading || !this.roles.length || !this.languages.length}>${t('userManagement.addRole')}</button>
                    </div>
                  </div>
                </div>
                <div class="form-actions">
                  <button type="button" class="btn-reset-mfa" data-testid="edit-user-reset-mfa" @click=${this.onResetMfa} ?disabled=${this.saving || this.editLoading || this.resetMfaLoading}>${this.resetMfaLoading ? t('userManagement.resettingMfa') : t('userManagement.resetMfa')}</button>
                  <button type="button" data-testid="edit-user-cancel" @click=${() => this.closeEdit()} ?disabled=${this.saving || this.editLoading}>${t('userManagement.cancel')}</button>
                  <button type="submit" data-testid="edit-user-save" ?disabled=${this.saving || this.editLoading}>${this.saving ? t('userManagement.saving') : t('userManagement.save')}</button>
                </div>
              </form>
            </div>
          ` : ''}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'user-management-page': UserManagementPage
  }
}
